import React, { useState, useRef } from 'react';
import PropTypes from "prop-types";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart"; // Changed to Line Chart
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart"; // ✅ Import VerticalBarChart
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import EntitiesPopup from "layouts/components/EntitiesPopup";
import { generateColors } from "utils/chartColors"; // ✅ Import Colors
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded"; // ✅ Import Icon


// -----------------------------------------------------------
// 🚀 HELPER FUNCTION
// -----------------------------------------------------------

/**
 * Extracts the daily reach count based on platform logic
 */
const getReachValue = (item, platform) => {
    if (!item) return 0;

    // Normalize platform to lowercase for consistent comparison
    const normPlatform = platform ? platform.toLowerCase() : "";

    // Facebook: Page Unique Impressions (Reach)
    if (normPlatform === "facebook") {
        return item.pageMetrics?.page_impressions_unique || 0;
    }

    // Instagram: Profile Reach
    if (normPlatform === "instagram") {
        return item.profileMetrics?.reach || 0;
    }

    // YouTube: Estimate Reach from Views/Impressions (Ratio 1.5)
    if (normPlatform === "youtube") {
        // Use channelMetrics (preferred) or sum of videos
        const impressions = item.channelMetrics?.views || item.channelMetrics?.impressions || 0;

        return Math.round(impressions / 1.5);
    }

    return 0;
};


// -----------------------------------------------------------
// 🚀 MAIN COMPONENT
// -----------------------------------------------------------

function AudienceReachChart({
    title,
    datasets,
    previousdatasets = [],
    futuredatasets = [],
    isPredictionEnabled = false,
    isLoading = false,
    isEntitled = true,   // ✅ Added
    featureName = "",    // ✅ Added
    planRequired = "",   // ✅ Added
    entitlements, // ✅ Added prop
    viewMode, // ✅ NEW
    onViewModeChange, // ✅ NEW
    profileColorMap = {} // ✅ Add profileColorMap prop (fallback empty)
}) {
    // ✅ State for the popup
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupDate, setPopupDate] = useState(null);

    // ✅ Click handlers for the popup
    const handlePointClick = (date) => {
        setPopupDate(date);
        setIsPopupOpen(true);
    };

    const handlePopupClose = () => {
        setIsPopupOpen(false);
        setPopupDate(null);
    };

    // ---------------------------------------------------------------------
    // RENDER LOADER
    // ---------------------------------------------------------------------
    const renderLoader = () => (
        <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                borderRadius: "0.75rem",
            }}
        >
            <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
    );

    const chartTitle = title || "Reach Over Time";

    // Safely fallback
    const safeDatasets = datasets || [];
    const safePreviousDatasets = previousdatasets || [];
    const safeFutureDatasets = futuredatasets || [];

    // ✅ Use provided Future Data if prediction is enabled
    // Note: AudienceCountChart handles logic slightly differently for merging, 
    // but here we stick to the pattern of having separate future datasets if needed.
    // However, to match AudienceCountChart structure more closely for Line Chart:
    const updatedPreviousDatasets = isPredictionEnabled
        ? [...safePreviousDatasets, ...safeDatasets]
        : safePreviousDatasets;

    // ✅ Align labels
    const actualLabels = safeDatasets.length > 0 ? safeDatasets[0].data.map((item) => item.date) : [];
    const predictedLabels = isPredictionEnabled && safeFutureDatasets.length > 0 && safeDatasets.length > 0
        ? safeFutureDatasets[0].data.slice(safeDatasets[0].data.length).map((item) => item.date)
        : [];
    const chartLabels = [...actualLabels, ...predictedLabels];

    // ✅ Construct Chart Data
    const computedChartData = {
        labels: chartLabels,
        datasets: [
            // 🔹 Actual Data
            ...safeDatasets.map((profile) => {
                const chartValues = [];
                // We don't have separate 'predictionFlags' logic deeply integrated in getReachValue 
                // like AudienceCountChart does with specific field checks, 
                // but we can map the data directly.

                profile.data.forEach((item) => {
                    chartValues.push(getReachValue(item, profile.platform));
                });

                return {
                    label: profile.name || profile.label,
                    data: chartValues,
                    profileId: profile.profileId,
                    profilePictureUrl: profile.profilePictureUrl || '',
                    platform: profile.platform || "Unknown",
                    metricName: "Reach",
                };
            }),

            // 🔹 Predicted Data
            ...(isPredictionEnabled ? safeFutureDatasets.map((profile) => {
                const chartValues = [];
                profile.data.forEach((item) => {
                    chartValues.push(getReachValue(item, profile.platform));
                });

                return {
                    label: `${profile.name || profile.label} (Predicted)`,
                    data: chartValues,
                    borderDash: [5, 5],
                    profileId: profile.profileId,
                    profilePictureUrl: profile.profilePictureUrl || '',
                    platform: profile.platform || "Unknown",
                    metricName: "Predicted Reach",
                };
            }) : []),
        ],

        // 🔹 Previous Data
        previousdatasets: updatedPreviousDatasets.map((profile) => {
            const chartValues = [];
            profile.data.forEach((item) => {
                chartValues.push(getReachValue(item, profile.platform));
            });
            return {
                label: profile.name || profile.label,
                data: chartValues,
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl || '',
                platform: profile.platform || "Unknown",
                metricName: "Reach",
            };
        }),
    };

    // -----------------------------------------------------------
    // PERSISTENCE LOGIC
    // -----------------------------------------------------------
    const lastChartDataRef = useRef(null);

    if (safeDatasets.length > 0) {
        lastChartDataRef.current = computedChartData;
    }

    let dataToRender = computedChartData;
    if (isLoading && safeDatasets.length === 0 && lastChartDataRef.current) {
        dataToRender = lastChartDataRef.current;
    }

    const hasData = (dataToRender.datasets && dataToRender.datasets.length > 0);

    // 🚀 CHECK IF ELIGIBLE FOR GRANULAR BREAKDOWN
    // Condition: All profiles must be Facebook AND Owned
    const isFacebookGranular = safeDatasets.length > 0 && safeDatasets.every(p =>
        p.platform === 'Facebook' && p.own === true
    );

    const generatedColors = generateColors(safeDatasets.length, safeDatasets.map(d => d.profileId));

    // Helper to add opacity
    const hexToRgba = (hex, alpha) => {
        let r = 0, g = 0, b = 0;
        if (hex.startsWith('#')) hex = hex.slice(1);
        if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        if (hex.length === 6) {
            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        }
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // 🚀 PREPARE STACKED DATA (If Granular)
    let stackedDatasets = [];
    if (isFacebookGranular) {
        safeDatasets.forEach((profile, index) => {
            const organicData = [];
            const paidData = [];

            // Base Color
            const baseColorHex = profileColorMap[profile.profileId] || generatedColors[index] || "#3b5998";
            const COLOR_ORGANIC = hexToRgba(baseColorHex, 0.9);
            const COLOR_PAID = hexToRgba(baseColorHex, 0.4);

            profile.data.forEach(item => {
                // ✅ Use User-Requested Metrics
                const paid = item.pageMetrics?.page_impressions_paid || 0;
                const organic = item.pageMetrics?.page_impressions_organic || 0;
                paidData.push(paid);
                organicData.push(organic);
            });

            const stackId = `stack-${profile.profileId}`;

            // 1. Organic (Base)
            stackedDatasets.push({
                label: profile.label || profile.name,
                data: organicData,
                backgroundColor: COLOR_ORGANIC,
                borderColor: baseColorHex,
                borderWidth: 1,
                stack: stackId,
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl,
                platform: profile.platform,
                metricName: "Organic Reach"
            });

            // 2. Paid (Top)
            stackedDatasets.push({
                label: profile.label || profile.name,
                data: paidData,
                backgroundColor: COLOR_PAID,
                borderColor: baseColorHex,
                borderWidth: 1,
                stack: stackId,
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl,
                platform: profile.platform,
                metricName: "Paid Reach",
                skipLegend: true
            });
        });
    }

    // 🚀 CUSTOM LEGEND (Deduplicated)
    const renderCustomLegend = () => {
        if (!hasData) return null;
        return (
            <MDBox display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" px={2} py={1}>
                {safeDatasets.map((profile, i) => {
                    const baseColor = profileColorMap[profile.profileId] || generatedColors[i] || "#3b5998";
                    return (
                        <MDBox key={i} display="flex" alignItems="center" mr={2} mb={1}>
                            <MDBox
                                sx={{
                                    width: 20,
                                    height: 10,
                                    borderRadius: "2px",
                                    backgroundColor: baseColor,
                                    mr: 1,
                                }}
                            />
                            <FacebookRoundedIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />
                            <MDTypography variant="button" color="text" sx={{ fontWeight: 300, ml: 0.5 }}>
                                {profile.name || profile.label || "Unknown Profile"}
                            </MDTypography>
                        </MDBox>
                    );
                })}
            </MDBox>
        );
    };

    // 🚀 CUSTOM METRICS
    const customMetrics = isFacebookGranular ? safeDatasets.map((profile) => {
        const organicTotal = profile.data.reduce((sum, item) => sum + (item.pageMetrics?.page_impressions_organic || 0), 0);
        const paidTotal = profile.data.reduce((sum, item) => sum + (item.pageMetrics?.page_impressions_paid || 0), 0);
        const total = organicTotal + paidTotal;

        // Previous Data (Total Reach)
        const prevProfile = safePreviousDatasets.find(p => p.profileId === profile.profileId);
        const prevTotal = prevProfile ? prevProfile.data.reduce((sum, item) => sum + (getReachValue(item, prevProfile.platform)), 0) : 0;

        return {
            profilePic: profile.profilePictureUrl || "https://via.placeholder.com/150",
            name: profile.name || profile.label || "Unknown Profile",
            platform: profile.platform || "Unknown Platform",
            metricName: "Total Reach",
            metricValue: total.toLocaleString(),
            previousMetricValue: prevTotal.toLocaleString(),
            isQualitative: false,
        };
    }) : null;

    // 🚀 OVERRIDE OPTIONS (Tooltip for Stacked Mode)
    const overrideOptions = isFacebookGranular ? {
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.metricName || context.dataset.label || '';
                        let value = context.parsed.y;
                        return `${label}: ${Number(value).toLocaleString()}`;
                    },
                    footer: (tooltipItems) => {
                        let total = 0;
                        tooltipItems.forEach(function (tooltipItem) {
                            total += tooltipItem.parsed.y;
                        });
                        return 'Total: ' + total.toLocaleString();
                    }
                }
            }
        }
    } : null;

    // ✅ Tooltip
    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                {isFacebookGranular
                    ? "This chart displays the daily Reach, broken down by Organic (free) and Paid (ads) sources."
                    : "This chart displays the daily Reach (Unique Viewers) for your profiles."
                }
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                {isFacebookGranular ? (
                    <>
                        - <strong>Organic (Darker):</strong> People reached naturally.<br />
                        - <strong>Paid (Lighter):</strong> People reached via ads.<br />
                        - <strong>Total Height:</strong> Total unique people reached.
                    </>
                ) : (
                    <>
                        - <strong>Rising Trend</strong>: Your audience base is expanding. <br />
                        - <strong>Spikes</strong>: Viral posts reaching new people. <br />
                    </>
                )}
            </Typography>
        </div>
    );

    return (
        <>
            {isLoading && renderLoader()}

            {hasData ? (
                isFacebookGranular ? (
                    <VerticalBarChart
                        title={chartTitle}
                        description={tooltipContent}
                        height="19.125rem"
                        chart={{ labels: chartLabels, datasets: stackedDatasets, previousdatasets: safePreviousDatasets }}
                        aggregate={true}
                        onBarClick={handlePointClick}
                        isEntitled={isEntitled}
                        featureName={featureName}
                        planRequired={planRequired}
                        rawData={datasets}
                        entitlements={entitlements}
                        viewMode={viewMode}
                        onViewModeChange={onViewModeChange}
                        stacked={true}
                        customMetrics={customMetrics}
                        customLegend={renderCustomLegend()}
                        overrideOptions={overrideOptions}
                    />
                ) : (
                    <DefaultLineChart
                        title={chartTitle}
                        description={tooltipContent}
                        height="19.125rem"
                        chart={dataToRender}
                        onPointClick={handlePointClick}
                        isEntitled={isEntitled}
                        featureName={featureName}
                        planRequired={planRequired}
                        aggregate // ✅ Enable aggregation (sum) for Total Reach
                        rawData={safeDatasets}
                        entitlements={entitlements} // ✅ Pass entitlements
                        viewMode={viewMode} // ✅ Pass down CORRECT PROP NAME
                        onViewModeChange={onViewModeChange} // ✅ Pass down
                        profileColorMap={profileColorMap}
                    />
                )
            ) : (
                <Card sx={{ height: "19.125rem", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {!isLoading && (
                        <MDTypography variant="body2" color="text">
                            No reach data available.
                        </MDTypography>
                    )}
                </Card>
            )}

            {isPopupOpen && (
                <EntitiesPopup
                    isModalOpen={isPopupOpen}
                    onClose={handlePopupClose}
                    datasets={datasets}
                    date={popupDate}
                />
            )}
        </>
    );
}

// 🛡️ PropTypes Validation
AudienceReachChart.propTypes = {
    title: PropTypes.string,
    datasets: PropTypes.array.isRequired,
    previousdatasets: PropTypes.array,
    futuredatasets: PropTypes.array,
    isPredictionEnabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    isEntitled: PropTypes.bool,
    featureName: PropTypes.string,
    planRequired: PropTypes.string,
    entitlements: PropTypes.object, // ✅ Added propType
    viewMode: PropTypes.string,
    onViewModeChange: PropTypes.func,
};

export default AudienceReachChart;
